// src/private/ScenarioTimeline.jsx
// scenari_futuri mode: a branching timeline rendered inside ZoneLayer's
// canvas-space layer (see ZoneLayer.js), replacing the tradizionale kanban
// columns / mappatura mind-map for this mode. Root "Oggi" node -> splits
// (fork points) -> branches (scenario labels), recursively nestable — any
// node at any depth can fork again (not just root).
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import ColorGrid from "./ColorGrid";
import { BRAND_PALETTE } from "./colorPalette";
import { computeScenarioNumbers } from "../lib/scenarioTreeSummary";
// useT from langContext (NOT LanguageContext.jsx): this module is imported
// by scenarioCardGrid.js (for TIMELINE_ORIGIN_OFFSET) purely for its layout
// math, and scenarioCardGrid.test.js imports it directly under jsdom.
// LanguageContext.jsx transitively pulls in firebase-config.jsx (via
// AuthContext), which breaks under jsdom (see InfiniteCanvas.js's own
// comment on this same problem) — langContext.js holds only the context
// object + useLang/useT, with no firebase import, so it's safe here while
// still tracking the profile language via full React context (unlike
// tStatic, which only reads localStorage and misses the profile).
import { useT } from "../i18n/langContext";
import "./ScenarioTimeline.css";

export const ROW_H = 60;       // vertical spacing between sibling branch rows
// Clearance above the first row for the floating top toolbar at the
// scenari_futuri area-jump's fixed zoom (AreaJumpButton.js, 0.30) — measured
// live via Playwright: the toolbar sits ~2 canvas-screen px above the region
// background's own top edge at that zoom, so a ~46-screen-px breathing gap
// below it (clear of the toolbar, not an empty void) needs ~160 canvas
// units. Exported so ZoneLayer.js can estimate the tree's rendered height
// without duplicating the layout algorithm (see countScenarioTreeRows below).
export const TIMELINE_ORIGIN_OFFSET = 160;
const ROOT_ID = "root";
const CONNECTOR_GAP = 72; // horizontal clearance between a column's widest pill and the next column
const LABEL_TRUNCATE_LEN = 50; // branch labels longer than this show "…" in view mode (full text on hover, double-click to edit)
// Estimated pill widths before their first real measurement lands (see
// useNodeWidths below) — close to typical real sizes so the one-time
// layout correction once ResizeObserver reports in is barely visible.
const ROOT_W_FALLBACK = 95;
const BRANCH_W_FALLBACK = 180;

// Smooth cubic-bezier connector between two points — the classic flowchart
// S-curve: control points sit at the horizontal midpoint, level with each
// endpoint's own y, so the curve degenerates gracefully to a near-straight
// line when the two points share a y.
function bezierPath(x0, y0, x1, y1) {
  const midX = (x0 + x1) / 2;
  return `M${x0},${y0} C${midX},${y0} ${midX},${y1} ${x1},${y1}`;
}

// Flattens the splits/branches graph into absolute-position rows (one per
// branch) plus split markers (kept only as an anchor for the pending
// accept/dismiss control now — connector routing no longer goes through
// them, see buildColumns). Any node with real children (root included) sits
// centered on the vertical midpoint of its own direct children — not at
// whatever row it happened to be visited on — so a parent stays visually
// balanced against its fork(s) how ever many branches develop under it.
// Only leaves (branches with no forks of their own) get sequential row
// slots; every ancestor's position is derived bottom-up from those. Returns
// totalRows = the leaf count, i.e. the number of row slots — the tree's
// full vertical extent is always exactly that many rows tall, since a
// centered parent's y can never exceed its deepest descendant leaf's.
function buildLayout(splits, branches) {
  const splitsByAfter = {};
  splits.forEach((s) => {
    (splitsByAfter[s.afterNodeId] = splitsByAfter[s.afterNodeId] || []).push(s);
  });
  const branchesBySplit = {};
  branches.forEach((b) => {
    (branchesBySplit[b.parentSplitId] = branchesBySplit[b.parentSplitId] || []).push(b);
  });
  const rows = [];    // { y, depth, split, branch }
  const markers = []; // { split, y } — x resolved later, once column edges are known
  let leafCursor = 0;

  // Post-order: lays out nodeId's children first, then returns nodeId's own
  // row-unit y (a leaf's sequential slot, or the midpoint of its children).
  function layout(nodeId, depth) {
    const childYs = [];
    (splitsByAfter[nodeId] || []).forEach((split) => {
      const branchesHere = branchesBySplit[split.id] || [];
      if (branchesHere.length === 0) return; // no rows to anchor a marker to
      const branchYs = branchesHere.map((branch) => {
        const y = layout(branch.id, depth + 1);
        rows.push({ y: y * ROW_H, depth, split, branch });
        return y;
      });
      markers.push({ split, y: ((Math.min(...branchYs) + Math.max(...branchYs)) / 2) * ROW_H });
      childYs.push(...branchYs);
    });
    if (childYs.length === 0) {
      const y = leafCursor;
      leafCursor += 1;
      return y;
    }
    return (Math.min(...childYs) + Math.max(...childYs)) / 2;
  }

  const rootY = layout(ROOT_ID, 0) * ROW_H;
  return { rows, markers, rootY, totalRows: leafCursor };
}

// Leaf-row count only, for callers (ZoneLayer.js) that just need the tree's
// vertical extent to reserve space below it — without needing the full
// per-node layout (column widths, connector paths, etc).
export function countScenarioTreeRows(splits, branches) {
  return buildLayout(splits, branches).totalRows;
}

// Estimated horizontal extent, same use case as countScenarioTreeRows but
// for width: callers outside this component (ZoneLayer.js) can't reach the
// real per-pill measurements buildColumns uses (those live in this
// component's own ResizeObserver state), so this mirrors buildColumns'
// math using the same fallback widths it falls back to before a real
// measurement lands — an estimate, not pixel-perfect, generously padded
// since a real label can render wider than the fallback guesses.
export function estimateScenarioTreeWidth(splits, branches) {
  const { rows } = buildLayout(splits, branches);
  const maxDepth = rows.reduce((m, r) => Math.max(m, r.depth), -1);
  if (maxDepth < 0) return ROOT_W_FALLBACK; // root only, no forks yet
  return ROOT_W_FALLBACK + (maxDepth + 1) * (BRANCH_W_FALLBACK + CONNECTOR_GAP);
}

// Column x-positions, sized to content: column 0 is the root's own left
// edge (0); each next column starts CONNECTOR_GAP past the widest measured
// pill in the column before it. This guarantees a connector's parent-edge
// start point never lands past its child-edge end point, however long a
// label gets — a fixed column width couldn't promise that once connectors
// attach to real edges instead of a shared fixed-x marker.
function buildColumns(rows, widths) {
  const maxWidthByCol = { 0: widths[ROOT_ID] ?? ROOT_W_FALLBACK };
  let maxCol = 0;
  rows.forEach(({ depth, branch }) => {
    const c = depth + 1;
    const w = widths[branch.id] ?? BRANCH_W_FALLBACK;
    if (w > (maxWidthByCol[c] ?? 0)) maxWidthByCol[c] = w;
    if (c > maxCol) maxCol = c;
  });
  const colX = { 0: 0 };
  for (let c = 1; c <= maxCol; c++) {
    colX[c] = colX[c - 1] + (maxWidthByCol[c - 1] ?? BRANCH_W_FALLBACK) + CONNECTOR_GAP;
  }
  return colX;
}

// View/edit toggle for a branch label (mirrors PostItNode.js's
// double-click-to-edit pattern): a long label would otherwise overflow the
// pill with no visual cue it's cut off (plain <input> text just clips,
// browsers don't show "…" on unfocused overflow) — view mode truncates at
// LABEL_TRUNCATE_LEN with a native title tooltip for the full text on
// hover, double-click swaps in the same editable input as before (local
// state for a responsive feel while typing, committed on blur/Enter).
function BranchLabelInput({ branch, onRename }) {
  const t = useT();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(branch.label || "");
  useEffect(() => { setValue(branch.label || ""); }, [branch.label]);
  const commit = () => {
    if (onRename && value !== branch.label) onRename(branch.id, value);
    setIsEditing(false);
  };

  if (!isEditing) {
    const label = branch.label || "";
    const truncated = label.length > LABEL_TRUNCATE_LEN
      ? `${label.slice(0, LABEL_TRUNCATE_LEN)}…`
      : label;
    return (
      <span
        className="scenario-node__label-text"
        title={label}
        onDoubleClick={() => setIsEditing(true)}
      >
        {truncated || t('canvas.scenarioTimeline.newScenarioPlaceholder')}
      </span>
    );
  }
  return (
    <input
      type="text"
      className="scenario-node__input"
      value={value}
      placeholder={t('canvas.scenarioTimeline.newScenarioPlaceholder')}
      aria-label={t('canvas.scenarioTimeline.labelAria')}
      autoFocus
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}

// Right-click popover: standard palette swatches + solid/dashed stroke
// toggle for highlighting one branch's own path. Portaled to document.body
// so its position:fixed coordinates aren't re-scaled by the canvas's own
// pan/zoom transform (an ancestor transform makes fixed descendants
// position relative to it, not the viewport — the portal escapes that).
function ScenarioColorMenu({ x, y, color, strokeStyle, onPick, onStroke, onDelete, onClose }) {
  const t = useT();
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={ref} className="scenario-color-menu" style={{ left: x, top: y }}>
      <ColorGrid colors={BRAND_PALETTE} value={color} onPick={onPick} allowNone />
      <div className="scenario-color-menu__strokes">
        <button
          type="button"
          className={`scenario-color-menu__stroke-btn${strokeStyle !== "dashed" ? " is-active" : ""}`}
          title={t('canvas.scenarioTimeline.solidStroke')}
          aria-label={t('canvas.scenarioTimeline.solidStroke')}
          onClick={() => onStroke("solid")}
        >
          ―
        </button>
        <button
          type="button"
          className={`scenario-color-menu__stroke-btn${strokeStyle === "dashed" ? " is-active" : ""}`}
          title={t('canvas.scenarioTimeline.dashedStroke')}
          aria-label={t('canvas.scenarioTimeline.dashedStroke')}
          onClick={() => onStroke("dashed")}
        >
          ┄
        </button>
      </div>
      {onDelete && (
        <button
          type="button"
          className="scenario-color-menu__delete"
          title={t('canvas.scenarioTimeline.deleteScenario')}
          aria-label={t('canvas.scenarioTimeline.deleteScenario')}
          onClick={onDelete}
        >
          🗑 {t('canvas.scenarioTimeline.deleteScenario')}
        </button>
      )}
    </div>,
    document.body
  );
}

// Measures each node pill's real rendered width via ResizeObserver (its own
// CSS layout width, unaffected by the ancestor pan/zoom `transform` — that's
// a paint-time effect, not a layout one) and keeps a stable per-nodeId ref
// callback so observers aren't torn down and recreated on every render.
function useNodeWidths() {
  const [widths, setWidths] = useState({});
  const refCallbacks = useRef(new Map());
  const observers = useRef(new Map());

  function getNodeRef(nodeId) {
    let cb = refCallbacks.current.get(nodeId);
    if (!cb) {
      cb = (el) => {
        const prevObs = observers.current.get(nodeId);
        if (prevObs) {
          prevObs.disconnect();
          observers.current.delete(nodeId);
        }
        if (!el) return;
        const obs = new ResizeObserver(() => {
          const w = el.offsetWidth;
          setWidths((prev) => (prev[nodeId] === w ? prev : { ...prev, [nodeId]: w }));
        });
        obs.observe(el);
        observers.current.set(nodeId, obs);
      };
      refCallbacks.current.set(nodeId, cb);
    }
    return cb;
  }

  return { widths, getNodeRef };
}

export default function ScenarioTimeline({
  bounds,
  splits = [],
  branches = [],
  onAddSplit,
  onAcceptSplit,
  onDismissSplit,
  onRenameBranch,
  onUpdateBranchStyle,
  onDeleteBranch,
  onSizeChange,
}) {
  const t = useT();
  const [colorMenu, setColorMenu] = useState(null); // {branchId, x, y}
  const { widths, getNodeRef } = useNodeWidths();
  const { rows, markers, rootY, totalRows } = buildLayout(splits, branches);
  // Sequential "1, 2, 3…" identifiers for the top-level scenarios (root's
  // direct children) — descendants inherit the same number (their level-1
  // ancestor's) but only the level-1 pill itself renders it, per the brief:
  // only the first level identifies distinct scenarios in this view.
  // computeScenarioNumbers is shared with ScenarioMatrix.jsx, which does
  // show every chip's inherited number — single source of truth so the two
  // views can't disagree.
  const scenarioNumbers = computeScenarioNumbers(splits, branches);
  const originX = bounds.x + 60;
  // Matches dtZones.js's KANBAN_TOP (270) — same screen-space clearance
  // problem as the kanban columns (the floating top toolbar sits above the
  // canvas at a fixed screen position; at the area-jump's fit-to-bounds zoom
  // level a small canvas-unit offset shrinks to just a few screen px),
  // live-verified with Playwright.
  const originY = bounds.y + TIMELINE_ORIGIN_OFFSET;
  const colX = buildColumns(rows, widths);

  // Real rendered extent (uses actual measured pill widths, not the
  // fallback guesses buildColumns falls back to before ResizeObserver
  // reports in) — reported to the parent (ZoneLayer.js) so the "03
  // Brainstorming" background and the matrix below it can size themselves
  // to the tree's TRUE width instead of an outside estimate that has no way
  // to know real label widths and was found (live) to under-count them.
  const contentWidth = rows.length > 0
    ? Math.max(...rows.map((r) => colX[r.depth + 1] + (widths[r.branch.id] ?? BRANCH_W_FALLBACK)))
    : (widths[ROOT_ID] ?? ROOT_W_FALLBACK);
  const contentHeight = totalRows * ROW_H;
  useEffect(() => {
    if (onSizeChange) onSizeChange({ width: contentWidth, height: contentHeight });
  }, [contentWidth, contentHeight, onSizeChange]);

  const rowByBranchId = {};
  rows.forEach((r) => { rowByBranchId[r.branch.id] = r; });

  // A node's own outgoing-connector anchor: its right edge (measured pill
  // width past its column's left edge), vertically centered — which for a
  // fully-rounded ("pill") shape is exactly the tip of its rounded end, so
  // this is also the geometrically correct touch point, not an approximation.
  function parentAnchor(nodeId) {
    if (nodeId === ROOT_ID) {
      return { x: widths[ROOT_ID] ?? ROOT_W_FALLBACK, y: rootY };
    }
    const r = rowByBranchId[nodeId];
    if (!r) return { x: 0, y: 0 }; // afterNodeId already validated upstream; defensive only
    const c = r.depth + 1;
    const w = widths[r.branch.id] ?? BRANCH_W_FALLBACK;
    return { x: colX[c] + w, y: r.y };
  }

  // Every connector is the same shape at every depth: a bezier from the
  // parent's right edge straight to this branch's own left edge — no more
  // shared fork-point routing now that there's no dot marking a merge.
  const connectors = rows.map(({ y, depth, split, branch }) => {
    const parent = parentAnchor(split.afterNodeId);
    return {
      key: `edge-${branch.id}`,
      pending: split.pending,
      color: branch.color,
      dashed: branch.strokeStyle === "dashed",
      d: bezierPath(parent.x, parent.y, colX[depth + 1], y),
    };
  });

  // Pending mentor-suggested splits still need one accept/dismiss control
  // per split — anchored at the same point its branches' connectors fan out
  // from (the parent's right edge), vertically centered on its own branches.
  const markerAnchors = markers.map((m) => {
    const anchor = parentAnchor(m.split.afterNodeId);
    return { split: m.split, x: anchor.x, y: m.y };
  });

  const menuBranch = colorMenu ? branches.find((b) => b.id === colorMenu.branchId) : null;

  return (
    <div className="scenario-timeline" style={{ left: originX, top: originY }}>
      <svg className="scenario-connectors" width="1" height="1" style={{ overflow: "visible" }}>
        {connectors.map(({ key, pending, d, color, dashed }) => (
          <path
            key={key}
            className={`scenario-connector${pending ? " is-pending" : ""}`}
            d={d}
            style={
              color || dashed
                ? { stroke: color || undefined, strokeDasharray: dashed ? "6 5" : undefined }
                : undefined
            }
          />
        ))}
      </svg>

      <div
        ref={getNodeRef(ROOT_ID)}
        className="scenario-node scenario-node--root"
        style={{ left: 0, top: rootY }}
      >
        {/* Display label only — the mentor-facing tree summary (scenarioTreeSummary.js)
            still hardcodes "Oggi" verbatim, translation-independent. */}
        <span className="scenario-node__label">{t('canvas.scenarioTimeline.rootLabel')}</span>
        <button
          type="button"
          className="scenario-node__biforca"
          title={t('canvas.scenarioTimeline.biforca')}
          aria-label={t('canvas.scenarioTimeline.biforca')}
          onClick={() => onAddSplit && onAddSplit(ROOT_ID)}
        >
          +
        </button>
      </div>

      {rows.map(({ y, depth, split, branch }) => {
        return (
          <React.Fragment key={branch.id}>
            <div
              ref={getNodeRef(branch.id)}
              className={`scenario-node scenario-node--branch${split.pending ? " is-pending" : ""}`}
              style={{ left: colX[depth + 1], top: y, borderColor: branch.color || undefined }}
              onContextMenu={(e) => {
                e.preventDefault();
                setColorMenu({ branchId: branch.id, x: e.clientX, y: e.clientY });
              }}
            >
              <span
                className={`scenario-node__dot${branch.createdBy === "mentor" ? " scenario-node__dot--mentor" : ""}`}
                style={{ background: branch.color || undefined }}
              />
              {depth === 0 && (
                <span className="scenario-node__number">{scenarioNumbers[branch.id]}</span>
              )}
              <BranchLabelInput branch={branch} onRename={onRenameBranch} />
              {!split.pending && (
                <button
                  type="button"
                  className="scenario-node__biforca"
                  title={t('canvas.scenarioTimeline.biforca')}
                  aria-label={t('canvas.scenarioTimeline.biforca')}
                  onClick={() => onAddSplit && onAddSplit(branch.id)}
                >
                  +
                </button>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {markerAnchors.map(({ split, x, y }) => (
        split.pending && (
          <div key={split.id} className="scenario-split-marker is-pending" style={{ left: x, top: y }}>
            <div className="scenario-split-marker__actions">
              <button
                type="button"
                className="scenario-split-marker__accept"
                title={t('canvas.scenarioTimeline.acceptSplit')}
                aria-label={t('canvas.scenarioTimeline.acceptSplit')}
                onClick={() => onAcceptSplit && onAcceptSplit(split.id)}
              >
                ✓
              </button>
              <button
                type="button"
                className="scenario-split-marker__dismiss"
                title={t('canvas.scenarioTimeline.dismissSplit')}
                aria-label={t('canvas.scenarioTimeline.dismissSplit')}
                onClick={() => onDismissSplit && onDismissSplit(split.id)}
              >
                ✕
              </button>
            </div>
          </div>
        )
      ))}

      {colorMenu && menuBranch && (
        <ScenarioColorMenu
          x={colorMenu.x}
          y={colorMenu.y}
          color={menuBranch.color || null}
          strokeStyle={menuBranch.strokeStyle || "solid"}
          onPick={(color) => onUpdateBranchStyle && onUpdateBranchStyle(colorMenu.branchId, { color })}
          onStroke={(strokeStyle) => onUpdateBranchStyle && onUpdateBranchStyle(colorMenu.branchId, { strokeStyle })}
          onDelete={onDeleteBranch ? () => { onDeleteBranch(colorMenu.branchId); setColorMenu(null); } : undefined}
          onClose={() => setColorMenu(null)}
        />
      )}
    </div>
  );
}
